import { AuditAction, AuditEntity, AuditFieldChange, AuditLog, LogLevel } from '@gooduncles/gu-app-schema';

import { refreshSessionIfNeeded } from 'src/utils/common';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';

const firebaseManager = FirebaseManager.getInstance();
const auditLogPath = 'auditLog';

// 스키마 패키지의 타입을 re-export
export type { AuditAction, AuditEntity, AuditFieldChange as FieldChange, AuditLog as AuditLogDoc };

/** 감사 로그 입력 파라미터 */
export interface AuditLogParams {
  action: AuditAction;
  entity: AuditEntity;
  entityId: string;
  summary: string;
  changes?: AuditFieldChange[];
  metadata?: Record<string, unknown>;
  level?: LogLevel;
  slack?: {
    channel?: '4-앱-알림' | 'dev' | '4-자동화' | '3-cs';
    showBody?: boolean;
  };
}

/**
 * 두 객체 사이의 변경된 필드를 추출한다.
 * @param before 변경 전 객체
 * @param after 변경 후 객체
 * @param fields 비교할 필드 목록 (지정하지 않으면 after의 모든 키를 비교)
 */
export function buildChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  fields?: string[],
): AuditFieldChange[] {
  const keys = fields ?? Object.keys(after);
  return keys
    .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
    .map((key) => ({
      field: key,
      before: before[key],
      after: after[key],
    }));
}

/**
 * 콘솔 운영자의 데이터 변경 작업을 기록하는 감사 로거.
 * ConsoleLogger와 동일한 싱글톤 패턴을 사용하며, uid/publicIp 정보를 공유한다.
 */
export class AuditLogger {
  private static instance: AuditLogger;
  private uid: string | null;
  private publicIp: string;

  private constructor() {
    this.publicIp = 'unknown';
    this.uid = null;
  }

  public static getInstance() {
    if (AuditLogger.instance === undefined) {
      AuditLogger.instance = new AuditLogger();
    }
    return AuditLogger.instance;
  }

  public setUid(uid: string | null) {
    this.uid = uid;
  }

  public setPublicIp(ip: string) {
    this.publicIp = ip;
  }

  /**
   * FirebaseManager가 발생시키는 자동 audit 로그용 entry point.
   * metadata.source = 'auto'를 부착해 BigQuery에서 manual 로그와 분리 가능하게 한다.
   */
  public logAuto(params: AuditLogParams) {
    return this.log({
      ...params,
      metadata: { ...(params.metadata ?? {}), source: 'auto' },
    });
  }

  /**
   * 감사 로그를 기록한다.
   */
  public log(params: AuditLogParams) {
    const { action, entity, entityId, summary, changes, metadata, level, slack } = params;
    const path = window.location.pathname;

    const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
    const doc: AuditLog = {
      action,
      entity,
      entityId,
      summary,
      level: level ?? 'log',
      sessionId: refreshSessionIfNeeded(),
      publicIp: this.publicIp,
      path,
      uid: this.uid,
      expireAt: new Date(Date.now() + SIXTY_DAYS_MS),
    };

    if (changes && changes.length > 0) {
      doc.changes = changes;
    }

    if (metadata) {
      doc.metadata = metadata;
    }

    if (slack?.channel || slack?.showBody) {
      doc.slack = slack;
    }

    return firebaseManager.createDoc(auditLogPath, undefined, doc);
  }
}
